import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiCalendar, FiUser, FiEye, FiClock, FiTag } from 'react-icons/fi';
import { getPublishedBlogs } from '../services/api';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 2rem;
`;

const SearchSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const SearchInput = styled.input`
  padding: 12px 20px;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 25px;
  font-size: 1rem;
  width: 300px;
  outline: none;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
  }
`;

const SearchButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const BlogsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const BlogCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const BlogImage = styled.div`
  height: 200px;
  background: ${props => props.image ? `url(${props.image})` : props.theme.colors.background};
  background-size: cover;
  background-position: center;
  position: relative;
`;

const BlogContent = styled.div`
  padding: 1.5rem;
`;

const BlogTitle = styled.h3`
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  line-height: 1.4;
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: ${props => props.theme.colors.primaryDark};
    }
  }
`;

const BlogExcerpt = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const BlogTags = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const Tag = styled.span`
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.primary};
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ReadMoreButton = styled(Link)`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: background 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
`;

const PageButton = styled.button`
  background: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.primary};
    color: white;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalBlogs, setTotalBlogs] = useState(0);

  const fetchBlogs = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 9,
        ...(search && { search })
      };
      
      console.log('Fetching blogs with params:', params);
      const data = await getPublishedBlogs(params);
      console.log('Received blog data:', data);
      setBlogs(data.blogs);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
      setTotalBlogs(data.total);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBlogs(1, searchQuery);
  };

  const handlePageChange = (page) => {
    fetchBlogs(page, searchQuery);
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading blogs...</LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Blog</Title>
        <Subtitle>
          Latest articles and insights about RPL and career development
        </Subtitle>
      </Header>

      <SearchSection>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <SearchInput
            type="text"
            placeholder="Search blogs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <SearchButton type="submit">
            <FiSearch />
            Search
          </SearchButton>
        </form>
      </SearchSection>

      {blogs.length === 0 ? (
        <EmptyState>
          <h3>No blogs found</h3>
          <p>{searchQuery ? `No blogs match your search for "${searchQuery}"` : 'No published blogs available yet.'}</p>
        </EmptyState>
      ) : (
        <>
          <BlogsGrid>
            {blogs.map((blog) => (
              <BlogCard key={blog._id}>
                {blog.featuredImage && (
                  <BlogImage image={blog.featuredImage} />
                )}
                <BlogContent>
                  <BlogTitle>
                    <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                  </BlogTitle>
                  
                  <BlogExcerpt>{blog.excerpt}</BlogExcerpt>
                  
                  <BlogMeta>
                    <MetaItem>
                      <FiUser />
                      {blog.author}
                    </MetaItem>
                    <MetaItem>
                      <FiCalendar />
                      {formatDate(blog.publishedAt || blog.createdAt)}
                    </MetaItem>
                    <MetaItem>
                      <FiClock />
                      {blog.readTime || 5} min read
                    </MetaItem>
                    {blog.views > 0 && (
                      <MetaItem>
                        <FiEye />
                        {blog.views} views
                      </MetaItem>
                    )}
                  </BlogMeta>
                  
                  {blog.tags && blog.tags.length > 0 && (
                    <BlogTags>
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <Tag key={index}>
                          <FiTag style={{ marginRight: '0.25rem' }} />
                          {tag}
                        </Tag>
                      ))}
                    </BlogTags>
                  )}
                  
                  <ReadMoreButton to={`/blog/${blog.slug}`}>
                    Read More
                  </ReadMoreButton>
                </BlogContent>
              </BlogCard>
            ))}
          </BlogsGrid>

          {totalPages > 1 && (
            <Pagination>
              <PageButton
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PageButton
                  key={page}
                  active={page === currentPage}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              ))}
              
              <PageButton
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </Container>
  );
};

export default BlogPage; 